import React, { FC } from 'react';
import { CommunityPerksFilter, MembershipDto, UserDto } from '@sweatpals/shared';
import { Box, Button, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Showable } from '@sweatpals/react-ui-kit';
import { observer } from 'mobx-react-lite';
import { communityProfilesService, membershipsService } from '@/modules/_core/services';
import { authStore } from '../../../../stores';
import { queryKeys } from '@/modules/_core/constants';
import { PerkCard } from '../perk-card/perk-card.component';
import { LockedPerksAvatars } from '../perk-card/locked-perks.component';
import {
	perksPlaceholderBoxStyles,
	perksPlaceholderButtonStyles,
	perksPlaceholderContentStyles,
	perksPlaceholderDescriptionStyles,
	perksPlaceholderTitleStyles,
	perksSectionStyles,
} from './perks-section.styles';
import CircularProgress from '@mui/material/CircularProgress';

export interface PerksSectionProps {
	hostData?: UserDto;
	onGetMembership?(): void;
	onLogin?(): void;
	communityMemberships?: MembershipDto[];
}

export const PerksSection: FC<PerksSectionProps> = observer(({ hostData, onGetMembership, onLogin, communityMemberships }) => {
	const currentUser = authStore.user;
	const { data: userMemberships } = useQuery(
		queryKeys.userMemberships(currentUser?.id),
		async () => membershipsService.getCurrentUserMemberships(),
		{
			enabled: !!currentUser,
		}
	);

	const currentCommunityUserMembership = userMemberships?.find(utm =>
		communityMemberships?.some(m => m.id === utm.membershipId)
	);

	const { data: perks, isFetching: isPerksFetching } = useQuery(
		queryKeys.hostRelated(hostData?.id, 'perks', !!currentUser, !!currentCommunityUserMembership),
		async () => {
			const filter: CommunityPerksFilter = {
				withMembershipPerks: !!currentCommunityUserMembership,
				page: 1,
				pageSize: Number.MAX_SAFE_INTEGER,
			};

			if (currentUser) {
				return communityProfilesService.getCommunityPerks(hostData?.id, filter);
			}

			return communityProfilesService.getPublicCommunityPerks(hostData?.id, filter);
		},
		{
			enabled: !!hostData,
			keepPreviousData: true,
		}
	);

	const { data: shortPerksWithMembership, isFetching: isShortMembershipPerksFetching } = useQuery(
		queryKeys.hostRelated(hostData?.id, 'shortPerksWithMembership'),
		async () => {
			const shortPerks = await communityProfilesService.getCommunityMemberShortPerks(hostData?.id, {
				withMembershipPerks: true,
			});

			return shortPerks.filter(sp => sp.onlyForMemberships);
		},
		{
			enabled: !!hostData && !currentCommunityUserMembership,
			keepPreviousData: true,
		}
	);

	const communityHasPerks = !isPerksFetching && perks?.list?.length;
	const communityHasMembershipPerks = !isShortMembershipPerksFetching && shortPerksWithMembership?.length;
	const userHasNoMembership = !currentCommunityUserMembership;
	const hasNoPerks = !isPerksFetching && !perks?.list?.length && !isShortMembershipPerksFetching && !shortPerksWithMembership?.length;

	return (
		<Box sx={perksSectionStyles}>
			<Showable show={isPerksFetching || isShortMembershipPerksFetching}>
				<Box
					sx={{
						width: '100%',
						height: '320px',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<CircularProgress sx={{ color: 'white' }} />
				</Box>
			</Showable>

			<Showable hide={isPerksFetching || isShortMembershipPerksFetching}>
				{/* Perks with or without memberships */}
				<Showable show={communityHasPerks}>
					{perks?.list?.map(perk => (
						<PerkCard
							key={perk.id}
							data={perk}
							currentMembership={currentCommunityUserMembership}
						/>
					))}
				</Showable>

				{/* Perk card promoting memberships */}
				<Showable show={userHasNoMembership && communityHasMembershipPerks}>
					{() => (
						<PerkCard
							hostData={hostData}
							data={{
								id: '1',
								name: 'More perks',
								description: 'Check out more perks from this host',
								communityId: hostData?.id,
								onlyForMemberships: true,
								order: 100,
								url: 'https://google.com',
								avatarId: '2',
								createdAt: new Date(),
								updatedAt: new Date(),
							}}
							blur
							currentUser={currentUser}
							onLogin={onLogin}
							onGetMembership={onGetMembership}
							currentMembership={currentCommunityUserMembership}
							perksWithMembership={shortPerksWithMembership}
						/>
					)}
				</Showable>

				{/* Placeholder promoting membership if all perks are for memberships */}
				<Showable show={userHasNoMembership && communityHasMembershipPerks && !communityHasPerks}>
					{() => (
						<>
							{shortPerksWithMembership.map(perk => (
								<PerkCard
									key={perk.id}
									data={{
										...perk,
										name: 'Perks on background',
										description: 'Get membership to see them',
										url: 'https://google.com',
									}}
								/>
							))}

							<Box sx={perksPlaceholderBoxStyles}>
								<Box sx={perksPlaceholderContentStyles}>
									<LockedPerksAvatars perks={shortPerksWithMembership} />

									<Typography sx={perksPlaceholderTitleStyles}>Available only with membership</Typography>

									<Typography sx={perksPlaceholderDescriptionStyles}>
										Perks are exclusive benefits that enhance your membership with special discounts, offers, and
										experiences from our trusted partners!
									</Typography>

									<Button sx={perksPlaceholderButtonStyles} onClick={currentUser ? onGetMembership : onLogin}>
										{currentUser ? `${userHasNoMembership ? 'Get' : 'My'} Membership` : 'Login'}
									</Button>
								</Box>
							</Box>
						</>
					)}
				</Showable>

				<Showable show={hasNoPerks}>
					<Typography
						sx={{
							color: 'white',
						}}
					>
						No perks available
					</Typography>
				</Showable>
			</Showable>
		</Box>
	);
});
